export class ServiceError extends Error {
	public readonly statusCode: HttpStatusCode;
	constructor(message: string, statusCode: HttpStatusCode, cause?: Error) {
		super(message);
		this.name = this.constructor.name;
		this.statusCode = statusCode;

		if (cause) this.cause = cause;
		Error.captureStackTrace?.(this, new.target);
	}
}

export class NotFoundError extends ServiceError {
	constructor(message: string = "Resource not found") {
		super(message, HttpStatusCode.NOT_FOUND);
	}
}

export class BadRequestError extends ServiceError {
	constructor(message: string = "Bad request", cause?: Error) {
		super(message, HttpStatusCode.BAD_REQUEST, cause);
	}
}

export class UnauthorizedError extends ServiceError {
	constructor(message: string = "Unauthorized") {
		super(message, HttpStatusCode.UNAUTHORIZED);
	}
}

export class ForbiddenError extends ServiceError {
	constructor(message: string = "Forbidden") {
		super(message, HttpStatusCode.FORBIDDEN);
	}
}
export class ConflictError extends ServiceError {
	constructor(message: string = "Conflict") {
		super(message, HttpStatusCode.CONFLICT);
	}
}

export class InternalServerError extends ServiceError {
	constructor(message: string = "Internal server error") {
		super(message, HttpStatusCode.INTERNAL_SERVER_ERROR);
	}
}

export class ServiceUnavailableError extends ServiceError {
	constructor(message: string = "Service unavailable") {
		super(message, HttpStatusCode.SERVICE_UNAVAILABLE);
	}
}

export class GatewayTimeoutError extends ServiceError {
	constructor(message: string = "Gateway timeout") {
		super(message, HttpStatusCode.GATEWAY_TIMEOUT);
	}
}

export class UnprocessableEntityError extends ServiceError {
	constructor(message: string = "Unprocessable entity") {
		super(message, HttpStatusCode.UNPROCESSABLE_ENTITY);
	}
}

export class TooManyRequestsError extends ServiceError {
	constructor(message: string = "Too many requests") {
		super(message, HttpStatusCode.TOO_MANY_REQUESTS);
	}
}

export class NotImplementedError extends ServiceError {
	constructor(message: string = "Not implemented") {
		super(message, HttpStatusCode.NOT_IMPLEMENTED);
	}
}

export enum HttpStatusCode {
	OK = 200,
	CREATED = 201,
	ACCEPTED = 202,
	NO_CONTENT = 204,
	BAD_REQUEST = 400,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	NOT_FOUND = 404,
	CONFLICT = 409,
	INTERNAL_SERVER_ERROR = 500,
	SERVICE_UNAVAILABLE = 503,
	GATEWAY_TIMEOUT = 504,
	UNPROCESSABLE_ENTITY = 422,
	TOO_MANY_REQUESTS = 429,
	NOT_IMPLEMENTED = 501,
	BAD_GATEWAY = 502,
}

/**
 * Return specific ServiceError based on status code
 *
 * @param error
 * @returns ServiceError
 * @example
 * ```ts
 * const error = new ServiceError("Not Found", 404);
 * const specificError = getErrorType(error);
 * console.log(specificError instanceof NotFoundError); // true
 * ```
 */

export const getErrorInstance = (error: {
	statusCode: number;
	message: string;
}): ServiceError => {
	switch (error.statusCode) {
		case HttpStatusCode.BAD_REQUEST:
			return new BadRequestError(error.message);

		case HttpStatusCode.NOT_FOUND:
			return new NotFoundError(error.message);

		case HttpStatusCode.UNAUTHORIZED:
			return new UnauthorizedError(error.message);

		case HttpStatusCode.FORBIDDEN:
			return new ForbiddenError(error.message);

		case HttpStatusCode.CONFLICT:
			return new ConflictError(error.message);

		case HttpStatusCode.INTERNAL_SERVER_ERROR:
			return new InternalServerError(error.message);

		case HttpStatusCode.SERVICE_UNAVAILABLE:
			return new ServiceUnavailableError(error.message);

		case HttpStatusCode.GATEWAY_TIMEOUT:
			return new GatewayTimeoutError(error.message);

		case HttpStatusCode.UNPROCESSABLE_ENTITY:
			return new UnprocessableEntityError(error.message);

		case HttpStatusCode.TOO_MANY_REQUESTS:
			return new TooManyRequestsError(error.message);

		case HttpStatusCode.NOT_IMPLEMENTED:
			return new NotImplementedError(error.message);
		default:
			return new NotFoundError(error.message);
	}
};
